import { useParams, useSearchParams } from "react-router-dom"
import { isPublishedId } from "library/id-helpers.mjs"
import { Button, Stack } from "@mui/material"
import { OpenSchedule } from "event-definitions"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useDialog } from "lib/@hooks/useDialog"
import Iconify from "minimals-template/components/Iconify"
import { parentFind } from "library/tree/index.mjs"
import { PlanRoot } from "library/tree/constants.mjs"
import { If } from "lib/@components/switch"
import { isShareLinkContentOverrideEnabled } from "routes/schedule/plugins/share-link-comments/share-link-content-overrides-feature-flag"
import { ApplyTailoringToSchedulesDialog } from "./apply-tailoring-to-schedules-dialog"

export function EditDeltaWhileInSchedule() {
    const { id } = useParams()
    const [searchParams, setSearchParams] = useSearchParams()
    const { editExtension, parentItem, payload, scheduleItem } = useBoundContext()
    const isInRegime = parentFind(parentItem, (c) => c[PlanRoot])
    const openApplyTailoringDialog = useDialog(<ApplyTailoringToSchedulesDialog />, {
        maxWidth: "md",
        fullWidth: true,
    })

    if (isPublishedId(id) || !isInRegime || !payload) return null

    if (editExtension) {
        return (
            <Stack direction="row" spacing={1} alignItems="center">
                {/* "Apply to other schedules" propagates this tailoring (content overrides + comment
                    justifications) across the regime, so it rides the share-link content-override rollout
                    flags (comments + content-overrides). "Exit Tailoring" stays ungated so editors can
                    always leave tailoring mode. */}
                <If value={isShareLinkContentOverrideEnabled()}>
                    <Button
                        onClick={applyToOtherSchedules}
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={<Iconify icon="mdi:content-copy" />}
                        data-cy="apply-tailoring-to-other-schedules-button"
                    >
                        Apply To Other Schedules
                    </Button>
                </If>
                <Button
                    onClick={exitTailoring}
                    size="small"
                    variant="outlined"
                    color="primary"
                    startIcon={<Iconify icon="mdi:exit-to-app" />}
                    data-cy="exit-tailoring-button"
                >
                    Exit Tailoring
                </Button>
            </Stack>
        )
    }

    return (
        <Button
            onClick={open}
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<Iconify icon="mdi:edit" />}
            data-cy="tailor-schedule-button"
        >
            Tailor Schedule
        </Button>
    )

    function open() {
        // Pass the regime-pinned version (FIQ-676): without it the tailoring editor loads the
        // latest published schedule as its baseline, so "undo tailoring" would apply newly
        // published values without the "update tailored schedule" action.
        OpenSchedule(id).raiseAsync({ id }, { editEx: true }, { version: scheduleItem?.version })
    }

    function applyToOtherSchedules() {
        return openApplyTailoringDialog({ currentId: id, payload })
    }

    function exitTailoring() {
        const nextSearchParams = new URLSearchParams(searchParams)
        nextSearchParams.delete("editEx")
        setSearchParams(nextSearchParams)
    }
}
